// Marketplace map for the landing page, ported from the inline script in
// public/landing/index.html.
//
// Leaflet and Leaflet.markercluster are loaded from a CDN on demand rather than
// bundled: markercluster is not a dependency of this project, and adding one is
// out of scope for the landing-page port. The app's own vue2-leaflet copy is not
// reused here because this map needs markercluster, which that wrapper does not
// provide. See docs/modernization/11-open-questions-and-assumptions.md.

// Pinned versions with Subresource Integrity hashes as published by the cdnjs
// API, so a tampered or swapped CDN file is rejected by the browser rather than
// executed. Update the hash whenever the version changes.
const LEAFLET_CSS = {
  href: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.css",
  integrity: "sha512-Zcn6bjR/8RZbLEpLIeOwNtzREBAJnUKESxces60Mpoj+2okopSAcSUIUOseddDm0cxnGQzxIR7vJgsLZbdLE3w==",
};
const LEAFLET_JS = {
  src: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/leaflet.js",
  integrity: "sha512-BwHfrr4c9kmRkLw6iXFdzcdWV/PGkVgiIyIWLLlTSXzWQzxuSg4DiQUCpauz/EWjgk5TYQqX/kvn9pG1NpYfqg==",
};
const CLUSTER_JS = {
  src: "https://cdnjs.cloudflare.com/ajax/libs/leaflet.markercluster/1.5.3/leaflet.markercluster.js",
  integrity: "sha512-OFs3W4DIZ5ZkrDhBFtsCP6JXtMEDGmhl0QPlmWYBJay40TT1n3gt2Xuw8Pf/iezgW9CdabjkNChRqozl/YADmg==",
};

// Street tiles: Stadia Maps "alidade_smooth" through Adverpost's existing tile
// proxy, as in the developer handoff (the app's own maps use the same proxy),
// and Stadia's matching dark style while the page is dark. The coastline
// outline is only a fallback, removed once the first tile loads.
const TILE_URLS = {
  light: "https://cors.adverpost.com/https/tiles.stadiamaps.com/tiles/alidade_smooth/{z}/{x}/{y}{r}.png",
  dark: "https://cors.adverpost.com/https/tiles.stadiamaps.com/tiles/alidade_smooth_dark/{z}/{x}/{y}{r}.png",
};
const TILE_ATTRIBUTION =
  '&copy; <a href="https://stadiamaps.com/" target="_blank" rel="noopener">Stadia Maps</a> ' +
  '&copy; <a href="https://openmaptiles.org/" target="_blank" rel="noopener">OpenMapTiles</a> ' +
  '&copy; <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap</a>';

// Shown on every bookable screen. data-auth is handled by LandingPage, which
// opens the app's register or login modal.
const BOOK_LINKS =
  '<a class="pop-cta" href="#/home" data-auth="register">Create an account to book</a>' +
  '<a class="pop-login" href="#/login" data-auth="login">I have an account</a>';

// Sample screen locations. Only the bookable (green) ones are drawn, until the
// real bookable screens load (and kept if they cannot be); grey pins are only
// ever real managed screens (showManagedScreens).
// [name, lat, lng, screens, spread, labelClass]
const TOWNS = [
  ["Adelaide", -34.93, 138.6, 46, 0.12, "big"],
  ["Mount Barker", -35.07, 138.86, 6, 0.03],
  ["Gawler", -34.6, 138.75, 4, 0.03],
  ["Tanunda", -34.52, 138.96, 6, 0.05],
  ["Clare", -33.83, 138.61, 5, 0.04],
  ["Victor Harbor", -35.55, 138.62, 5, 0.04],
  ["Murray Bridge", -35.12, 139.27, 4, 0.03],
  ["Berri", -34.28, 140.6, 5, 0.06],
  ["Renmark", -34.17, 140.75, 3, 0.03],
  ["Port Augusta", -32.49, 137.77, 3, 0.03],
  ["Whyalla", -33.03, 137.58, 3, 0.03],
  ["Port Pirie", -33.18, 138.01, 3, 0.03],
  ["Port Lincoln", -34.73, 135.86, 5, 0.04],
  ["Mount Gambier", -37.83, 140.78, 5, 0.04],
  ["Kadina", -33.96, 137.72, 2, 0.03],
  ["Naracoorte", -36.96, 140.74, 2, 0.03],
  ["Melbourne", -37.81, 144.96, 8, 0.08],
  ["Ballarat", -37.56, 143.85, 2, 0.03],
  ["Albury", -36.08, 146.92, 2, 0.03],
  ["Los Angeles", 34.05, -118.24, 4, 0.15],
  ["Dallas", 32.78, -96.8, 3, 0.12],
  ["New York", 40.71, -74.0, 3, 0.1],
  ["Shanghai", 31.23, 121.47, 4, 0.12],
  ["Shenzhen", 22.54, 114.06, 3, 0.08],
  ["Beijing", 39.9, 116.4, 2, 0.1],
];

const LABELLED = [
  "Adelaide", "Tanunda", "Clare", "Berri", "Victor Harbor", "Port Lincoln",
  "Port Augusta", "Mount Gambier", "Melbourne", "Los Angeles", "Dallas",
  "New York", "Shanghai", "Shenzhen", "Beijing",
];

const RENAMED = { Tanunda: "Barossa", Berri: "Riverland" };

// State borders drawn as dashed lines.
const BORDERS = [
  [[-26, 129], [-31.69, 129]],
  [[-26, 129], [-26, 141]],
  [[-26, 141], [-38.06, 141]],
  [[-26, 129], [-14.9, 129]],
  [[-26, 138], [-16.5, 138]],
  [[-29, 141], [-29, 148.9]],
];

const REGION_LABELS = [
  ["SOUTH AUSTRALIA", [-29.6, 133.2], "st"],
  ["AUSTRALIA", [-24, 128], "ctry"],
  ["UNITED STATES", [45, -108], "ctry"],
  ["CHINA", [38, 88], "ctry"],
  ["VICTORIA", [-37.2, 143.4], "st"],
  ["NEW SOUTH WALES", [-32.6, 144.2], "st"],
  ["WESTERN AUSTRALIA", [-28.4, 123.4], "st"],
];

function loadScript({ src, integrity }) {
  return new Promise((resolve, reject) => {
    const existing = document.querySelector(`script[src="${src}"]`);
    if (existing) {
      if (existing.dataset.loaded) resolve();
      else existing.addEventListener("load", () => resolve());
      existing.addEventListener("error", reject);
      return;
    }
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    // SRI only applies to cross-origin requests made in CORS mode.
    s.integrity = integrity;
    s.crossOrigin = "anonymous";
    s.referrerPolicy = "no-referrer";
    s.addEventListener("load", () => {
      s.dataset.loaded = "1";
      resolve();
    });
    s.addEventListener("error", reject);
    document.head.appendChild(s);
  });
}

function loadCss({ href, integrity }) {
  if (document.querySelector(`link[href="${href}"]`)) return;
  const l = document.createElement("link");
  l.rel = "stylesheet";
  l.href = href;
  l.integrity = integrity;
  l.crossOrigin = "anonymous";
  l.referrerPolicy = "no-referrer";
  document.head.appendChild(l);
}

/** Load Leaflet + markercluster once, in order. */
export async function loadLeaflet() {
  loadCss(LEAFLET_CSS);
  if (!window.L) await loadScript(LEAFLET_JS);
  if (window.L && !window.L.markerClusterGroup) await loadScript(CLUSTER_JS);
  return window.L;
}

// How long to wait for the first street tile before drawing the coastline
// outline instead (it also draws at once if a tile fails).
const FALLBACK_DELAY_MS = 1500;

const OPEN_LABEL = '<span style="color:#16A34A;font-weight:600">Open for booking</span>';
const MANAGED_POPUP = "<b>Managed screen</b>Run on Adverpost";

/**
 * Build the marketplace map inside `host`, with sample bookable pins.
 * `loadCoastlines` returns a promise of { AU, WORLD } GeoJSON; it is only
 * called if the street tiles fail or are slow, since the outline is just a
 * fallback. Returns { map, cluster, tiles, icons, sampleBookable }; call
 * map.__teardown() before map.remove().
 */
export function buildMap(host, L, { dark = false, loadCoastlines } = {}) {
  const map = L.map(host, {
    scrollWheelZoom: true,
    wheelPxPerZoomLevel: 90,
    dragging: true,
    tap: false,
    zoomSnap: 0.5,
    minZoom: 1.5,
    maxZoom: 18,
    worldCopyJump: true,
    maxBounds: [[-80, -200], [85, 220]],
    attributionControl: true,
  }).setView([-34.6, 138.3], 7);

  map.attributionControl.setPrefix(false);

  // Everything drawn only until the first street tile arrives.
  const fallback = [];
  let tilesUp = false;
  let removed = false;
  let fallbackTimer = null;

  const tiles = L.tileLayer(dark ? TILE_URLS.dark : TILE_URLS.light, {
    maxZoom: 19,
    attribution: TILE_ATTRIBUTION,
  }).addTo(map);
  tiles.once("tileload", () => {
    tilesUp = true;
    clearTimeout(fallbackTimer);
    fallback.forEach((layer) => map.removeLayer(layer));
    fallback.length = 0; // release the layers (the coastlines are large)
  });

  // className lets the dark theme recolour land and borders from CSS.
  const landStyle = (weight) => ({
    color: "#C9D2DE",
    weight,
    fillColor: "#FFFFFF",
    fillOpacity: 1,
    className: "land",
  });
  let coastlinesRequested = false;
  const drawCoastlines = () => {
    if (tilesUp || coastlinesRequested || !loadCoastlines) return;
    coastlinesRequested = true;
    loadCoastlines()
      .then(({ AU, WORLD }) => {
        if (tilesUp || removed) return;
        const land = [
          L.geoJSON({ type: "Feature", geometry: WORLD }, { style: landStyle(1), interactive: false }),
          L.geoJSON({ type: "Feature", geometry: AU }, { style: landStyle(1.2), interactive: false }),
        ];
        land.forEach((layer) => {
          layer.addTo(map).bringToBack();
          fallback.push(layer);
        });
      })
      .catch(() => {});
  };
  tiles.once("tileerror", drawCoastlines);
  fallbackTimer = setTimeout(drawCoastlines, FALLBACK_DELAY_MS);

  BORDERS.forEach((line) =>
    fallback.push(
      L.polyline(line, { color: "#C9D2DE", weight: 1.2, dashArray: "5 5", interactive: false, className: "border" }).addTo(map)
    )
  );

  const label = (text, latlng, cls) =>
    fallback.push(
      L.marker(latlng, {
        interactive: false,
        keyboard: false,
        icon: L.divIcon({
          className: "",
          html: `<span class="town ${cls || ""}">${text}</span>`,
          iconSize: [0, 0],
          iconAnchor: [-24, 7],
        }),
      }).addTo(map)
    );

  REGION_LABELS.forEach(([t, ll, c]) => label(t, ll, c));

  // Zoom-dependent label density, driven by classes on the host element.
  const applyZoomClass = () => {
    const z = map.getZoom();
    host.classList.toggle("z-world", z < 4.5);
    host.classList.toggle("z-mid", z >= 4.5 && z < 6);
  };
  map.on("zoomend", applyZoomClass);
  applyZoomClass();

  const blockWheel = (e) => e.preventDefault();
  host.addEventListener("wheel", blockWheel, { passive: false });

  // Deterministic pseudo-random placement so the map looks identical on every
  // load (Lehmer generator, same seed as the original page).
  let seed = 11;
  const rnd = () => {
    seed = (seed * 16807) % 2147483647;
    return seed / 2147483647;
  };

  const cluster = L.markerClusterGroup({
    showCoverageOnHover: false,
    maxClusterRadius: 46,
    spiderfyOnMaxZoom: true,
    iconCreateFunction: (c) => {
      const n = c.getChildCount();
      return L.divIcon({
        className: "",
        html: `<div class="clu${n < 6 ? " sm" : ""}"><b>${n}</b></div>`,
        iconSize: [40, 40],
      });
    },
  });

  // One icon per kind of pin, shared by every marker of that kind.
  const pinIcon = (cls) => L.divIcon({ className: "", html: `<div class="${cls}"></div>`, iconSize: [12, 12], iconAnchor: [6, 6] });
  const icons = { bookable: pinIcon("pin pub"), managed: pinIcon("pin") };

  // The sample bookable pins, replaced by real screens once those load.
  const sampleBookable = [];

  TOWNS.forEach((t) => {
    const [name, lat0, lng0, count, spread, cls] = t;
    if (LABELLED.includes(name)) label(RENAMED[name] || name, [lat0, lng0], cls);

    for (let i = 0; i < count; i++) {
      const angle = rnd() * Math.PI * 2;
      const dist = Math.sqrt(rnd()) * spread;
      const lat = lat0 + Math.sin(angle) * dist * 0.8;
      let lng = lng0 + Math.cos(angle) * dist;
      // Keep Adelaide pins off the gulf.
      if (name === "Adelaide" && lng < 138.51) lng = 138.51 + rnd() * 0.04;

      // Every sample still draws its random numbers, so the green pins keep
      // the places they had when grey samples were drawn too.
      if (rnd() >= 0.3) continue;

      const marker = L.marker([lat, lng], { icon: icons.bookable }).bindPopup(
        `<b>${name}${i ? " #" + (i + 1) : ""}</b>${OPEN_LABEL}<br>From $0.06 per second${BOOK_LINKS}`
      );
      cluster.addLayer(marker);
      sampleBookable.push(marker);
    }
  });

  map.addLayer(cluster);

  // Let the caller detach the wheel handler and the fallback timer with the map.
  map.__teardown = () => {
    removed = true;
    clearTimeout(fallbackTimer);
    host.removeEventListener("wheel", blockWheel);
  };
  return { map, cluster, tiles, icons, sampleBookable };
}

/** Switch the street tiles between the light and dark styles. */
export function setMapTheme(built, dark) {
  // setUrl does nothing when the URL is unchanged.
  built.tiles.setUrl(dark ? TILE_URLS.dark : TILE_URLS.light);
}

const escapeHtml = (value) =>
  String(value == null ? "" : value).replace(
    /[&<>"']/g,
    (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c])
  );

// price_per_second is stored in cents, as everywhere else in the app (see the
// money filter in src/lib/filters.js), so it is converted before display.
const perSecond = (cents, currency) => {
  const dollars = parseFloat(cents) / 100;
  if (!isFinite(dollars) || dollars <= 0) return "";
  const amount = dollars.toLocaleString("en-AU", { minimumFractionDigits: 2, maximumFractionDigits: 3 });
  return (currency && currency !== "AUD" ? escapeHtml(currency) + " " : "$") + amount;
};

/** [lat, lng] from an API row (numbers or strings), or null if unusable. */
const latLngOf = (d) => {
  const lat = parseFloat(d.latitude);
  const lng = parseFloat(d.longitude);
  if (!isFinite(lat) || !isFinite(lng) || (lat === 0 && lng === 0)) return null;
  return [lat, lng];
};

/**
 * Replace the sample bookable pins with real bookable screens, as rows from
 * GET v1/displays?...&public=true. Names and types are entered by customers,
 * so everything placed in the popup is escaped. With no rows, the sample pins
 * stay.
 */
export function showScreens(built, L, displays) {
  const pins = [];
  (displays || []).forEach((d) => {
    const at = latLngOf(d);
    if (!at) return;
    const price = perSecond(d.baselinePrice && d.baselinePrice.price_per_second, d.currency);
    pins.push(
      L.marker(at, { icon: built.icons.bookable }).bindPopup(
        `<b>${escapeHtml(d.name || "Screen")}</b>${OPEN_LABEL}` +
          (d.type ? "<br>" + escapeHtml(d.type) : "") +
          (price ? "<br>From " + price + " per second" : "") +
          BOOK_LINKS
      )
    );
  });
  if (!pins.length) return;
  built.cluster.removeLayers(built.sampleBookable);
  built.sampleBookable = [];
  built.cluster.addLayers(pins);
}

// The New Display wizard's default location, kept by screens whose location
// was never set: One World LED's old office on South Road, Melrose Park. It
// was -34.98385, 138.57395 until 2025 and -34.9839, 138.574 since.
const DEFAULT_LOCATIONS = [
  [-34.98385, 138.57395],
  [-34.9839, 138.574],
];
const isDefaultLocation = ([lat, lng]) =>
  DEFAULT_LOCATIONS.some(([dlat, dlng]) => Math.abs(lat - dlat) < 0.00011 && Math.abs(lng - dlng) < 0.00011);

/**
 * Add the managed (not bookable) screens as grey pins, from
 * GET v1/displays/private?..., which returns only { displayId, latitude,
 * longitude } (coordinates as strings). One pin per screen, so screens at the
 * same site add up in the cluster counts, except the screens still on the
 * wizard's default location: like the old home-page map, which drew them all
 * on one spot, they appear as a single pin. Screens also in `bookableIds` are
 * skipped.
 */
export function showManagedScreens(built, L, displays, bookableIds = []) {
  const skip = new Set(bookableIds);
  const pin = (at) => L.marker(at, { icon: built.icons.managed }).bindPopup(MANAGED_POPUP);
  const pins = [];
  let unlocated = false;
  (displays || []).forEach((d) => {
    if (skip.has(d.displayId)) return;
    const at = latLngOf(d);
    if (!at) return;
    if (isDefaultLocation(at)) unlocated = true;
    else pins.push(pin(at));
  });
  if (unlocated) pins.push(pin(DEFAULT_LOCATIONS[1]));
  if (pins.length) built.cluster.addLayers(pins);
}
